import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./AgendaCalendarView.css";
import {
  format,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  addYears,
  subYears,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  eachDayOfInterval,
  eachMonthOfInterval,
  isSameMonth,
  isSameDay,
  isSameYear,
  isToday,
  parseISO,
  startOfDay,
  endOfDay,
  getHours,
  getMinutes,
} from "date-fns";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import {
  CALENDAR_DAY_NAMES,
  MAX_VISIBLE_CALENDAR_LANES,
  getCategoryIcon,
} from "../../constants/agendaConstants";
import {
  filterEventsByCategory,
  layoutWeekEvents,
  layoutTimedEvents,
  expandRecurringEvents,
} from "../../utils/agendaUtils";
import AgendaScheduleHoverCard from "./AgendaScheduleHoverCard";
import AgendaDaySchedulesPopover from "./AgendaDaySchedulesPopover";

const VIEWS = ["Day", "Week", "Month", "Year"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const formatMinutesLabel = (min) => {
  const clamped = Math.max(0, Math.min(min, 24 * 60 - 1));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return format(new Date(2000, 0, 1, h, m), "h:mm a");
};

const isEventOnDay = (event, day) => {
  try {
    const eventStart = startOfDay(parseISO(event.date));
    const eventEnd = event.endDate ? endOfDay(parseISO(event.endDate)) : endOfDay(eventStart);
    const selectedDay = startOfDay(day);
    return selectedDay >= eventStart && selectedDay <= eventEnd;
  } catch {
    return false;
  }
};

// True when an event spans more than one calendar day — these render in the
// "All-day" strip of Day/Week view instead of the hourly time grid.
const isEventMultiDay = (event) => {
  if (!event?.date || !event?.endDate) return false;
  return event.date !== event.endDate;
};

const eventsOverlappingRange = (events, rangeStart, rangeEnd) =>
  events.filter((event) => {
    try {
      const start = startOfDay(parseISO(event.date));
      const end = event.endDate ? endOfDay(parseISO(event.endDate)) : endOfDay(start);
      return start <= rangeEnd && end >= rangeStart;
    } catch {
      return false;
    }
  });

export default function AgendaCalendarView({ events, onSelectEvent, selectedCategory }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("Month");
  const [slideDirection, setSlideDirection] = useState("next");
  const [schedulePreview, setSchedulePreview] = useState(null);
  const [daySchedules, setDaySchedules] = useState(null);
  const previewDismissTimer = useRef(null);
  const timeGridRef = useRef(null);

  // The time grid now renders full-height (no internal scroll box), so bring
  // the current hour into view on the page itself when opening Day/Week.
  useEffect(() => {
    if ((view === "Day" || view === "Week") && timeGridRef.current) {
      const hour = getHours(new Date());
      const target = timeGridRef.current.querySelector(
        `[data-hour="${Math.max(0, hour - 1)}"]`
      );
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [view, currentDate]);

  const clearPreviewDismissTimer = useCallback(() => {
    if (previewDismissTimer.current) {
      clearTimeout(previewDismissTimer.current);
      previewDismissTimer.current = null;
    }
  }, []);

  const closeSchedulePreview = useCallback(() => {
    clearPreviewDismissTimer();
    setSchedulePreview(null);
  }, [clearPreviewDismissTimer]);

  const closeDaySchedules = useCallback(() => {
    closeSchedulePreview();
    setDaySchedules(null);
  }, [closeSchedulePreview]);

  const queueSchedulePreviewClose = useCallback(
    (delay = 120) => {
      clearPreviewDismissTimer();
      previewDismissTimer.current = setTimeout(() => {
        setSchedulePreview(null);
        previewDismissTimer.current = null;
      }, delay);
    },
    [clearPreviewDismissTimer]
  );

  const queueSideSchedulePreviewClose = useCallback(
    () => queueSchedulePreviewClose(300),
    [queueSchedulePreviewClose]
  );

  const showSchedulePreview = useCallback(
    (event, anchor, placement = "vertical", sideAnchor = null) => {
      clearPreviewDismissTimer();
      setSchedulePreview({ event, anchor, placement, sideAnchor });
    },
    [clearPreviewDismissTimer]
  );

  useEffect(
    () => () => clearPreviewDismissTimer(),
    [clearPreviewDismissTimer]
  );

  useEffect(() => {
    closeDaySchedules();
  }, [currentDate, selectedCategory, view, closeDaySchedules]);

  // Navigation
  const navigate = (direction) => {
    setSlideDirection(direction);
    setCurrentDate((prev) => {
      if (view === "Day") return direction === "next" ? addDays(prev, 1) : subDays(prev, 1);
      if (view === "Week") return direction === "next" ? addWeeks(prev, 1) : subWeeks(prev, 1);
      if (view === "Month") return direction === "next" ? addMonths(prev, 1) : subMonths(prev, 1);
      return direction === "next" ? addYears(prev, 1) : subYears(prev, 1);
    });
  };

  const goToToday = () => {
    const today = new Date();
    setSlideDirection(today >= currentDate ? "next" : "prev");
    setCurrentDate(today);
  };

  // Date range for visible events
  const { rangeStart, rangeEnd } = useMemo(() => {
    if (view === "Day") return { rangeStart: startOfDay(currentDate), rangeEnd: endOfDay(currentDate) };
    if (view === "Week") {
      return {
        rangeStart: startOfWeek(currentDate, { weekStartsOn: 1 }),
        rangeEnd: endOfWeek(currentDate, { weekStartsOn: 1 }),
      };
    }
    if (view === "Year") return { rangeStart: startOfYear(currentDate), rangeEnd: endOfYear(currentDate) };
    const ms = startOfMonth(currentDate);
    const me = endOfMonth(ms);
    return {
      rangeStart: startOfWeek(ms, { weekStartsOn: 1 }),
      rangeEnd: endOfWeek(me, { weekStartsOn: 1 }),
    };
  }, [view, currentDate]);

  const visibleEvents = useMemo(() => {
    const filtered = filterEventsByCategory(events, selectedCategory);
    return expandRecurringEvents(filtered, rangeStart, rangeEnd);
  }, [events, selectedCategory, rangeStart, rangeEnd]);

  const getDayEvents = useCallback(
    (day) =>
      visibleEvents
        .filter((e) => isEventOnDay(e, day))
        .sort((a, b) => (a.startTime || "00:00").localeCompare(b.startTime || "00:00")),
    [visibleEvents]
  );

  const showDaySchedules = useCallback(
    (day, anchor) => {
      closeSchedulePreview();
      setDaySchedules({ day, anchor });
    },
    [closeSchedulePreview]
  );

  // Nav title
  const navTitle = useMemo(() => {
    if (view === "Day") return format(currentDate, "EEE, MMM d, yyyy");
    if (view === "Week") {
      const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
      const we = endOfWeek(currentDate, { weekStartsOn: 1 });
      return isSameMonth(ws, we)
        ? `${format(ws, "MMM d")} – ${format(we, "d, yyyy")}`
        : `${format(ws, "MMM d")} – ${format(we, "MMM d, yyyy")}`;
    }
    if (view === "Year") return format(currentDate, "yyyy");
    return format(currentDate, "MMMM yyyy");
  }, [view, currentDate]);

  // ── Month view data ──────────────────────────────────────────────────────────
  const monthData = useMemo(() => {
    if (view !== "Month") return null;
    const ms = startOfMonth(currentDate);
    const me = endOfMonth(ms);
    const start = startOfWeek(ms, { weekStartsOn: 1 });
    const end = endOfWeek(me, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    return { weeks, monthStart: ms };
  }, [view, currentDate]);

  // ── Week view data ───────────────────────────────────────────────────────────
  const weekDays = useMemo(() => {
    if (view !== "Week") return null;
    const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: ws, end: addDays(ws, 6) });
  }, [view, currentDate]);

  // ── Year view data ───────────────────────────────────────────────────────────
  const yearMonths = useMemo(() => {
    if (view !== "Year") return null;
    return eachMonthOfInterval({ start: startOfYear(currentDate), end: endOfYear(currentDate) });
  }, [view, currentDate]);

  // A year grid has ~371 day cells (12 months × up to ~6 weeks). Checking
  // each one against `visibleEvents` with a linear `.filter()` — as the mini
  // month grid used to — is O(days × events), which is what made switching
  // to Year feel slow/janky. Build a plain `yyyy-MM-dd -> has events` Set
  // once instead, so each cell below is an O(1) lookup.
  const yearEventDateSet = useMemo(() => {
    if (view !== "Year") return null;
    const set = new Set();
    visibleEvents.forEach((event) => {
      try {
        const start = startOfDay(parseISO(event.date));
        const end = event.endDate ? endOfDay(parseISO(event.endDate)) : endOfDay(start);
        let cursor = start;
        let guard = 0;
        while (cursor <= end && guard < 400) {
          set.add(format(cursor, "yyyy-MM-dd"));
          cursor = addDays(cursor, 1);
          guard += 1;
        }
      } catch {
        // ignore malformed dates
      }
    });
    return set;
  }, [visibleEvents, view]);

  const renderEventPill = (evt, key, extra = {}) => (
    <div
      key={key}
      className={`acv-event-pill acv-pill-${evt.type}`}
      style={extra.style}
      role="button"
      tabIndex={0}
      onMouseEnter={(e) => showSchedulePreview(evt, e.currentTarget)}
      onMouseLeave={() => queueSchedulePreviewClose()}
      onFocus={(e) => showSchedulePreview(evt, e.currentTarget)}
      onBlur={() => queueSchedulePreviewClose()}
      onClick={() => { closeDaySchedules(); onSelectEvent(evt); }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); closeDaySchedules(); onSelectEvent(evt); }
        if (e.key === "Escape") closeSchedulePreview();
      }}
    >
      {getCategoryIcon(evt.type, 12)}
      <span>{evt.title}</span>
    </div>
  );

  // Timed time-grid pill: shows title + time range, and adapts to how many
  // lanes it shares so it never overflows into an unreadable sliver.
  const renderTimedEventPill = (evt, key, style) => {
    const compact = evt.totalCols >= 3;
    return (
      <div
        key={key}
        className={`acv-time-pill acv-pill-${evt.type} ${compact ? "compact" : ""}`}
        style={style}
        role="button"
        tabIndex={0}
        onMouseEnter={(e) => showSchedulePreview(evt, e.currentTarget)}
        onMouseLeave={() => queueSchedulePreviewClose()}
        onFocus={(e) => showSchedulePreview(evt, e.currentTarget)}
        onBlur={() => queueSchedulePreviewClose()}
        onClick={() => { closeDaySchedules(); onSelectEvent(evt); }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); closeDaySchedules(); onSelectEvent(evt); }
          if (e.key === "Escape") closeSchedulePreview();
        }}
      >
        <span className="acv-time-pill-title">{evt.title}</span>
        {!compact && evt.endMin - evt.startMin >= 34 && (
          <span className="acv-time-pill-time">{formatMinutesLabel(evt.startMin)}</span>
        )}
      </div>
    );
  };

  const renderOverflowChip = (group, day) => (
    <div
      key={group.key}
      className="acv-time-overflow-chip"
      style={{
        position: "absolute",
        top: group.startMin,
        // Anchor at the cluster's start and cap the height — a cluster can span
        // hours, but the "+N more" tag itself should stay a compact chip.
        height: Math.min(Math.max(group.endMin - group.startMin, 24), 64),
        width: `calc(${100 / group.totalCols}% - 4px)`,
        left: `calc(${(group.col / group.totalCols) * 100}% + 2px)`,
      }}
      role="button"
      tabIndex={0}
      onClick={(e) => showDaySchedules(day, e.currentTarget)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); showDaySchedules(day, e.currentTarget); }
      }}
    >
      +{group.count} more
    </div>
  );

  // ── DAY VIEW ─────────────────────────────────────────────────────────────────
  const renderDayView = () => {
    const dayEvts = getDayEvents(currentDate);
    const allDayEvts = dayEvts.filter(isEventMultiDay);
    const timedDayEvts = dayEvts.filter((e) => !isEventMultiDay(e));
    const { placed, overflowGroups } = layoutTimedEvents(timedDayEvts, 30, 5);
    const now = new Date();
    const nowMin = isToday(currentDate) ? getHours(now) * 60 + getMinutes(now) : -1;

    return (
      <div className="acv-day-view">
        {allDayEvts.length > 0 && (
          <div className="acv-allday-row">
            <div className="acv-week-gutter acv-allday-label">All-day</div>
            <div className="acv-allday-track">
              {allDayEvts.map((evt) =>
                renderEventPill(evt, `day-allday-${evt.id}`, { style: {} })
              )}
            </div>
          </div>
        )}
        <div className="acv-time-grid-wrapper" ref={timeGridRef}>
          <div className="acv-time-grid">
            {/* Current time indicator */}
            {nowMin >= 0 && (
              <div className="acv-now-line" style={{ top: `${nowMin}px` }}>
                <span className="acv-now-dot" />
              </div>
            )}
            {HOURS.map((h) => (
              <div key={h} data-hour={h} className="acv-hour-row" style={{ top: `${h * 60}px` }}>
                <span className="acv-hour-label">{h === 0 ? "" : format(new Date(2000, 0, 1, h), "h a")}</span>
                <div className="acv-hour-line" />
              </div>
            ))}
            <div className="acv-events-col">
              {placed.map((evt) => {
                const style = {
                  position: "absolute",
                  top: evt.startMin,
                  height: Math.max(evt.endMin - evt.startMin, 22),
                  width: `calc(${100 / evt.totalCols}% - 4px)`,
                  left: `calc(${(evt.col / evt.totalCols) * 100}% + 2px)`,
                };
                return renderTimedEventPill(evt, `day-${evt.id}`, style);
              })}
              {overflowGroups.map((group) => renderOverflowChip(group, currentDate))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── WEEK VIEW ────────────────────────────────────────────────────────────────
  const renderWeekView = () => {
    const now = new Date();
    const nowMin = getHours(now) * 60 + getMinutes(now);

    const weekRangeEvents = eventsOverlappingRange(
      visibleEvents,
      startOfDay(weekDays[0]),
      endOfDay(weekDays[6])
    );
    const multiDayEvents = weekRangeEvents.filter(isEventMultiDay);
    const { placedEvents: placedAllDay, hiddenCountByCol } = layoutWeekEvents(
      multiDayEvents,
      startOfDay(weekDays[0]),
      MAX_VISIBLE_CALENDAR_LANES,
      7
    );
    const hasAllDay = placedAllDay.length > 0 || hiddenCountByCol.some((c) => c > 0);

    return (
      <div className="acv-week-view">
        {/* Day headers */}
        <div className="acv-week-header">
          <div className="acv-week-gutter" />
          {weekDays.map((day, i) => (
            <button
              key={i}
              type="button"
              className={`acv-week-day-head ${isToday(day) ? "today" : ""}`}
              onClick={() => { setView("Day"); setCurrentDate(day); }}
              title={`Go to ${format(day, "EEEE, MMM d")}`}
            >
              <span className="acv-week-day-name">{format(day, "EEE")}</span>
              <span className={`acv-week-day-num ${isToday(day) ? "today-circle" : ""}`}>
                {format(day, "d")}
              </span>
            </button>
          ))}
        </div>

        {/* All-day / multi-day strip */}
        {hasAllDay && (
          <div className="acv-allday-row acv-allday-row-week">
            <div className="acv-week-gutter acv-allday-label">All-day</div>
            <div
              className="acv-allday-week-grid"
              style={{ gridTemplateRows: `repeat(${MAX_VISIBLE_CALENDAR_LANES + 1}, auto)` }}
            >
              {placedAllDay.map((evt) => (
                <div
                  key={`week-allday-${evt.id}`}
                  className={`acv-month-event-pill acv-pill-${evt.type} ${!evt.isStartThisWeek ? "continues-prev" : ""} ${!evt.isEndThisWeek ? "continues-next" : ""}`}
                  style={{ gridColumn: `${evt.startCol} / span ${evt.span}`, gridRow: evt.laneIndex + 1 }}
                  role="button"
                  tabIndex={0}
                  onMouseEnter={(e) => showSchedulePreview(evt, e.currentTarget)}
                  onMouseLeave={() => queueSchedulePreviewClose()}
                  onFocus={(e) => showSchedulePreview(evt, e.currentTarget)}
                  onBlur={() => queueSchedulePreviewClose()}
                  onClick={() => { closeDaySchedules(); onSelectEvent(evt); }}
                >
                  {evt.isStartThisWeek && getCategoryIcon(evt.type, 12)}
                  <span>{evt.title}</span>
                </div>
              ))}
              {hiddenCountByCol.map((count, colIdx) =>
                count > 0 ? (
                  <div
                    key={`week-more-${colIdx}`}
                    className="acv-more-pill"
                    style={{ gridColumn: colIdx + 1, gridRow: MAX_VISIBLE_CALENDAR_LANES + 1 }}
                    onClick={(e) => showDaySchedules(weekDays[colIdx], e.currentTarget)}
                  >
                    +{count} more
                  </div>
                ) : null
              )}
            </div>
          </div>
        )}

        {/* Time grid */}
        <div className="acv-time-grid-wrapper" ref={timeGridRef}>
          <div className="acv-week-grid">
            {/* Hour labels */}
            <div className="acv-week-gutter-col">
              {HOURS.map((h) => (
                <div key={h} data-hour={h} className="acv-week-hour-label" style={{ top: `${h * 60}px` }}>
                  {h === 0 ? "" : format(new Date(2000, 0, 1, h), "h a")}
                </div>
              ))}
            </div>
            {/* Day columns */}
            {weekDays.map((day, colIdx) => {
              const dayEvts = getDayEvents(day).filter((e) => !isEventMultiDay(e));
              // Cap at 2 real lanes per day column — a week column is narrow,
              // so 3-way splits shrink to unreadable slivers. Anything beyond
              // 2 overlapping events collapses into the "+N more" chip.
              const { placed, overflowGroups } = layoutTimedEvents(dayEvts, 30, 2);
              return (
                <div key={colIdx} className={`acv-week-col ${isToday(day) ? "today-col" : ""}`}>
                  {/* Hour lines */}
                  {HOURS.map((h) => (
                    <div key={h} className="acv-week-hour-line" style={{ top: `${h * 60}px` }} />
                  ))}
                  {/* Now line */}
                  {isToday(day) && (
                    <div className="acv-now-line" style={{ top: `${nowMin}px` }}>
                      <span className="acv-now-dot" />
                    </div>
                  )}
                  {placed.map((evt) => {
                    const style = {
                      position: "absolute",
                      top: evt.startMin,
                      height: Math.max(evt.endMin - evt.startMin, 22),
                      width: `calc(${100 / evt.totalCols}% - 4px)`,
                      left: `calc(${(evt.col / evt.totalCols) * 100}% + 2px)`,
                    };
                    return renderTimedEventPill(evt, `week-${evt.id}-col${colIdx}`, style);
                  })}
                  {overflowGroups.map((group) => renderOverflowChip(group, day))}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ── MONTH VIEW ───────────────────────────────────────────────────────────────
  const renderMonthView = () => {
    const { weeks, monthStart } = monthData;
    return (
      <>
        <div className="acv-month-day-names">
          {CALENDAR_DAY_NAMES.map((d, i) => (
            <div key={i} className="acv-month-day-name">{d}</div>
          ))}
        </div>
        <div
          key={`cal-grid-${format(currentDate, "yyyy-MM")}`}
          className={`acv-month-grid ${slideDirection === "next" ? "calendar-slide-next" : "calendar-slide-prev"}`}
        >
          {weeks.map((week, wIdx) => {
            const weekStart = startOfDay(week[0]);
            const weekEnd = endOfDay(week[6]);
            const weekEvents = visibleEvents.filter((e) => {
              try {
                const s = startOfDay(parseISO(e.date));
                const en = e.endDate ? endOfDay(parseISO(e.endDate)) : endOfDay(s);
                return s <= weekEnd && en >= weekStart;
              } catch { return false; }
            });
            const { placedEvents, hiddenCountByCol } = layoutWeekEvents(weekEvents, weekStart, MAX_VISIBLE_CALENDAR_LANES);

            return (
              <div key={wIdx} className="acv-month-week">
                {week.map((day, colIdx) => {
                  const isCurrentMonth = isSameMonth(day, monthStart);
                  const isDayToday = isToday(day);
                  return (
                    <div
                      key={colIdx}
                      className={`acv-month-cell ${!isCurrentMonth ? "other-month" : ""} ${isDayToday ? "today" : ""} ${daySchedules && isSameDay(day, daySchedules.day) ? "selected" : ""}`}
                      style={{ gridColumn: colIdx + 1, gridRow: "1 / -1" }}
                    >
                      <button
                        type="button"
                        className="acv-day-select-btn"
                        aria-label={`Show schedules for ${format(day, "d MMMM, yyyy")}`}
                        onClick={(e) => showDaySchedules(day, e.currentTarget)}
                      />
                      <div className="acv-month-cell-header">
                        <span className="acv-month-date-num">{format(day, "d")}</span>
                      </div>
                    </div>
                  );
                })}

                {placedEvents.map((evt) => (
                  <div
                    key={`${evt.id}-w${wIdx}`}
                    className={`acv-month-event-pill acv-pill-${evt.type} ${!evt.isStartThisWeek ? "continues-prev" : ""} ${!evt.isEndThisWeek ? "continues-next" : ""}`}
                    style={{ gridColumn: `${evt.startCol} / span ${evt.span}`, gridRow: evt.laneIndex + 2 }}
                    role="button"
                    tabIndex={0}
                    onMouseEnter={(e) => showSchedulePreview(evt, e.currentTarget)}
                    onMouseLeave={() => queueSchedulePreviewClose()}
                    onFocus={(e) => showSchedulePreview(evt, e.currentTarget)}
                    onBlur={() => queueSchedulePreviewClose()}
                    onClick={() => { closeDaySchedules(); onSelectEvent(evt); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); closeDaySchedules(); onSelectEvent(evt); }
                      if (e.key === "Escape") closeSchedulePreview();
                    }}
                  >
                    {evt.isStartThisWeek && getCategoryIcon(evt.type, 12)}
                    <span>{evt.title}</span>
                  </div>
                ))}

                {hiddenCountByCol.map((count, colIdx) =>
                  count > 0 ? (
                    <div
                      key={`more-${colIdx}`}
                      className="acv-more-pill"
                      style={{ gridColumn: colIdx + 1, gridRow: MAX_VISIBLE_CALENDAR_LANES + 2 }}
                      onClick={(e) => showDaySchedules(week[colIdx], e.currentTarget)}
                    >
                      +{count} more
                    </div>
                  ) : null
                )}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // ── YEAR VIEW ────────────────────────────────────────────────────────────────
  const renderMiniMonth = (monthDate) => {
    const ms = startOfMonth(monthDate);
    const me = endOfMonth(ms);
    const start = startOfWeek(ms, { weekStartsOn: 1 });
    const end = endOfWeek(me, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });
    const isCurrentViewMonth = isSameMonth(monthDate, currentDate) && isSameYear(monthDate, currentDate);

    return (
      <div
        key={format(monthDate, "yyyy-MM")}
        className={`acv-mini-month ${isCurrentViewMonth ? "active-mini-month" : ""}`}
      >
        <button
          type="button"
          className="acv-mini-month-title"
          onClick={() => { setView("Month"); setCurrentDate(monthDate); }}
        >
          {format(monthDate, "MMMM")}
        </button>
        <div className="acv-mini-grid">
          {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => (
            <div key={i} className="acv-mini-day-name">{d}</div>
          ))}
          {days.map((day, i) => {
            const hasEvents = yearEventDateSet?.has(format(day, "yyyy-MM-dd"));
            const inMonth = isSameMonth(day, ms);
            return (
              <button
                type="button"
                key={i}
                className={`acv-mini-day ${!inMonth ? "other-month" : ""} ${isToday(day) ? "today" : ""}`}
                onClick={() => { setView("Day"); setCurrentDate(day); }}
                title={`Go to ${format(day, "EEEE, MMM d, yyyy")}`}
              >
                {format(day, "d")}
                {inMonth && hasEvents && <span className="acv-mini-dot" />}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderYearView = () => (
    <div className="acv-year-grid">
      {yearMonths.map((m) => renderMiniMonth(m))}
    </div>
  );

  return (
    <div className="acv-container">
      {/* Top Nav */}
      <div className="acv-nav-bar">
        <div className="acv-nav-left">
          <div className="d-flex align-items-center gap-1 gap-sm-2 flex-shrink-0">
            <button className="acv-nav-btn" onClick={() => navigate("prev")} aria-label="Previous">
              <LuChevronLeft size={16} />
            </button>
            <button className="acv-nav-btn" onClick={() => navigate("next")} aria-label="Next">
              <LuChevronRight size={16} />
            </button>
            <button className="acv-today-btn" onClick={goToToday}>
              Today
            </button>
          </div>
          <h3
            key={navTitle}
            className={`acv-nav-title text-truncate ${slideDirection === "next" ? "calendar-slide-next" : "calendar-slide-prev"}`}
          >
            {navTitle}
          </h3>
        </div>
        <div className="acv-view-switcher">
          {VIEWS.map((v) => (
            <button
              key={v}
              className={`acv-view-btn ${view === v ? "active" : ""}`}
              onClick={() => setView(v)}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* View Content */}
      <div className="acv-body">
        {view === "Day" && renderDayView()}
        {view === "Week" && renderWeekView()}
        {view === "Month" && renderMonthView()}
        {view === "Year" && renderYearView()}
      </div>

      {schedulePreview && (
        <AgendaScheduleHoverCard
          event={schedulePreview.event}
          anchor={schedulePreview.anchor}
          placement={schedulePreview.placement}
          sideAnchor={schedulePreview.sideAnchor}
          onClose={closeSchedulePreview}
          onPointerEnter={clearPreviewDismissTimer}
          onPointerLeave={() => queueSchedulePreviewClose()}
          onViewDetails={(event) => { closeDaySchedules(); onSelectEvent(event); }}
        />
      )}

      {daySchedules && (
        <AgendaDaySchedulesPopover
          anchor={daySchedules.anchor}
          date={daySchedules.day}
          events={getDayEvents(daySchedules.day)}
          onClose={closeDaySchedules}
          onSelectEvent={(event) => { closeDaySchedules(); onSelectEvent(event); }}
          onShowSchedulePreview={showSchedulePreview}
          onHideSchedulePreview={queueSideSchedulePreviewClose}
        />
      )}
    </div>
  );
}
